import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Phone, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/chairs', label: 'Chairs' },
  { to: '/tables', label: 'Tables' },
  { to: '/tents', label: 'Tents' },
  { to: '/kiddies', label: 'Kiddies' },
  { to: '/accessories', label: 'Accessories' },
  { to: '/packages', label: 'Packages' },
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/blog', label: 'Blog' },
];

const CartButton = ({ onClick, totalItems, className, iconOnly }) => (
  <motion.button
    whileHover={iconOnly ? undefined : { scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={className}
    data-testid={iconOnly ? 'mobile-cart-button' : 'cart-button'}
  >
    <ShoppingCart size={20} />
    {!iconOnly && <span>Cart</span>}
    {totalItems > 0 && (
      <span className={`absolute ${iconOnly ? '-top-1 -right-1 w-5 h-5' : '-top-2 -right-2 w-6 h-6'} bg-violet-600 text-white text-xs font-bold rounded-full flex items-center justify-center`} data-testid={iconOnly ? 'mobile-cart-count' : 'cart-count'}>
        {totalItems}
      </span>
    )}
  </motion.button>
);

const DesktopNav = ({ pathname, totalItems, onCartOpen }) => (
  <div className="hidden lg:flex items-center gap-4">
    {NAV_LINKS.map((link) => (
      <Link
        key={link.to}
        to={link.to}
        className={`text-sm font-semibold hover:text-orange-500 transition-colors whitespace-nowrap ${
          pathname === link.to ? 'text-orange-500' : 'text-violet-700'
        }`}
        data-testid={`nav-${link.label.toLowerCase()}-link`}
      >
        {link.label}
      </Link>
    ))}
    <CartButton
      onClick={onCartOpen}
      totalItems={totalItems}
      className="relative bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg flex items-center gap-2 transition-colors whitespace-nowrap"
    />
  </div>
);

const MobileMenu = ({ isOpen, onClose, pathname }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-[55] lg:hidden"
        />
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.3 }}
          className="fixed top-0 right-0 h-screen w-72 bg-white shadow-2xl z-[60] lg:hidden overflow-y-auto"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-bold heading-font" style={{ color: '#7C3AED' }}>Menu</h2>
              <button onClick={onClose} className="p-2 text-gray-500 hover:text-orange-500 transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={onClose}
                  className={`block px-4 py-3 rounded-xl font-semibold text-lg transition-all ${
                    pathname === link.to
                      ? 'bg-orange-100 text-orange-600'
                      : 'text-violet-700 hover:bg-orange-50 hover:text-orange-500'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200">
              <a
                href="tel:484-957-0076"
                className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-semibold"
              >
                <Phone size={20} />
                <span>Call: 484-957-0076</span>
              </a>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const Navbar = () => {
  const { getTotalItems, setIsCartOpen } = useCart();
  const location = useLocation();
  const totalItems = getTotalItems();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-xl border-b-4 border-orange-400" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <Link to="/" className="group" data-testid="nav-logo-link">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full blur-md opacity-60 group-hover:opacity-80 transition-opacity"></div>
                <img
                  src="/logo.png"
                  alt="Thomas Party & Decor"
                  className="relative h-16 w-16 sm:h-20 sm:w-20 rounded-full shadow-2xl ring-4 ring-white group-hover:scale-105 transition-transform object-cover"
                  width="80"
                  height="80"
                  fetchPriority="high"
                />
              </div>
            </Link>
            <div>
              <Link to="/" className="block" data-testid="nav-title-link">
                <p className="text-xl sm:text-3xl font-bold heading-font whitespace-nowrap tracking-tight" style={{ color: '#7C3AED' }}>
                  Thomas Party & Decor
                </p>
              </Link>
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline-block bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">PARTY RENTALS</span>
                <a
                  href="tel:484-957-0076"
                  className="flex items-center gap-1 text-orange-600 hover:text-orange-700 font-bold text-sm sm:text-base transition-colors"
                >
                  <Phone size={16} className="hidden sm:block" />
                  <span>484-957-0076</span>
                </a>
              </div>
            </div>
          </div>

          <DesktopNav pathname={location.pathname} totalItems={totalItems} onCartOpen={() => setIsCartOpen(true)} />

          <div className="flex lg:hidden items-center gap-2">
            <CartButton
              onClick={() => setIsCartOpen(true)}
              totalItems={totalItems}
              className="relative bg-orange-500 hover:bg-orange-600 text-white p-2.5 rounded-full shadow-lg transition-colors"
              iconOnly
            />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-violet-700 hover:text-orange-500 transition-colors"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} pathname={location.pathname} />
    </nav>
  );
};

export default Navbar;
